import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/layout/Layout";
import { fetchSchedule, rescheduleWorkOrder } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { TypePill, PriorityPill } from "@/components/Pills";
import { toast } from "sonner";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // mon=0
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - day);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export default function Schedule() {
  const { user } = useAuth();
  const canDrag = user?.role === "admin" || user?.role === "technician";
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [items, setItems] = useState([]);
  const [dragOver, setDragOver] = useState(null);

  const weekEnd = addDays(weekStart, 7);

  const reload = () =>
    fetchSchedule(weekStart.toISOString(), weekEnd.toISOString()).then(setItems);

  useEffect(() => {
    reload();
  }, [weekStart]);

  const onDragStart = (e, wo) => {
    if (!canDrag) return;
    e.dataTransfer.setData("text/plain", wo.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = async (e, dayIndex) => {
    e.preventDefault();
    setDragOver(null);
    const wo_id = e.dataTransfer.getData("text/plain");
    if (!wo_id) return;
    const target = addDays(weekStart, dayIndex);
    // preserve original time
    const wo = items.find((x) => x.id === wo_id);
    let newDate = new Date(target);
    if (wo?.scheduled_for) {
      const orig = new Date(wo.scheduled_for);
      newDate.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
    } else {
      newDate.setHours(9, 0, 0, 0);
    }
    try {
      await rescheduleWorkOrder(wo_id, newDate.toISOString());
      toast.success(`Rescheduled to ${target.toLocaleDateString()}`);
      reload();
    } catch {
      toast.error("Reschedule failed");
    }
  };

  const byDay = useMemo(() => {
    const buckets = Array.from({ length: 7 }, () => []);
    items.forEach((w) => {
      const ref = w.scheduled_for || w.due_date;
      if (!ref) return;
      const d = new Date(ref);
      for (let i = 0; i < 7; i++) {
        if (sameDay(addDays(weekStart, i), d)) {
          buckets[i].push(w);
          break;
        }
      }
    });
    return buckets;
  }, [items, weekStart]);

  const fmt = (d) =>
    d.toLocaleDateString(undefined, { month: "short", day: "numeric" });

  const today = new Date();

  return (
    <Layout
      title="Schedule"
      breadcrumbs={["Operations", "Weekly Schedule"]}
      actions={
        <div className="flex items-center gap-2">
          <button
            data-testid="cal-prev"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="p-2 rounded-sm border border-zinc-300 hover:bg-zinc-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="cal-today"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="px-3 h-9 rounded-sm border border-zinc-300 hover:bg-zinc-50 text-xs font-bold uppercase tracking-wider"
          >
            <CalendarDays className="h-3.5 w-3.5 inline mr-1" /> Today
          </button>
          <button
            data-testid="cal-next"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="p-2 rounded-sm border border-zinc-300 hover:bg-zinc-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      }
    >
      <div className="mb-3 text-sm text-zinc-600 font-mono">
        {fmt(weekStart)} — {fmt(addDays(weekStart, 6))} · {items.length} scheduled
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {DAYS.map((d, i) => {
          const day = addDays(weekStart, i);
          const isToday = sameDay(day, today);
          return (
            <div
              key={i}
              data-testid={`cal-col-${i}`}
              onDragOver={(e) => {
                if (!canDrag) return;
                e.preventDefault();
                setDragOver(i);
              }}
              onDragLeave={() => setDragOver((cur) => (cur === i ? null : cur))}
              onDrop={(e) => onDrop(e, i)}
              className={`engineered-card flex flex-col min-h-[260px] transition-colors ${
                isToday ? "border-orange-500 ring-2 ring-orange-100" : ""
              } ${dragOver === i ? "border-orange-500 bg-orange-50" : ""}`}
            >
              <div className="px-3 py-2 border-b border-zinc-200 flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500">
                    {d}
                  </div>
                  <div className="font-mono font-bold text-lg leading-tight">
                    {day.getDate()}
                  </div>
                </div>
                {byDay[i].length > 0 && (
                  <span className="text-[10px] font-mono font-bold text-orange-600">
                    {byDay[i].length}
                  </span>
                )}
              </div>
              <div className="p-2 space-y-2 flex-1 overflow-y-auto">
                {byDay[i].map((w) => (
                  <Link
                    key={w.id}
                    to={`/work-orders/${w.id}`}
                    data-testid={`cal-wo-${w.number}`}
                    draggable={canDrag}
                    onDragStart={(e) => onDragStart(e, w)}
                    className={`block border border-zinc-200 rounded-sm p-2 hover:border-orange-500 transition-colors bg-white ${
                      canDrag ? "cursor-grab active:cursor-grabbing" : ""
                    }`}
                  >
                    <div className="flex items-center justify-between text-[10px] font-mono">
                      <span className="font-bold">{w.number}</span>
                      <PriorityPill priority={w.priority} />
                    </div>
                    <div className="text-xs font-semibold text-zinc-900 mt-1 line-clamp-2">
                      {w.title}
                    </div>
                    <div className="mt-1">
                      <TypePill type={w.type} />
                    </div>
                  </Link>
                ))}
                {byDay[i].length === 0 && (
                  <div className="text-[11px] text-zinc-400 text-center py-3">
                    {canDrag ? "drop here" : "nothing"}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Layout>
  );
}
